/**
 * <plp-gender-switch> — Men / Women on licensed (merch) PLPs (snippets/plp-gender-switch.liquid).
 *
 * A side applies its gender (plus shared values like Unisex) on top of the current
 * filters; the open side clears it. The URL is built from the live query on click,
 * because desktop filters rewrite it over AJAX after the page renders.
 */
// The section loads this before the page's import map, so `@theme/events` can't
// resolve here; this is ThemeEvents.FilterUpdate, the event facets.js dispatches.
const FILTER_UPDATE_EVENT = 'filter:update';

class PlpGenderSwitch extends HTMLElement {
  connectedCallback() {
    this.param = this.dataset.param;
    this.options = Array.from(this.querySelectorAll('[data-gender-values]'));

    this.addEventListener('click', (event) => {
      const option = event.target.closest('[data-gender-values]');
      if (!option) return;
      event.preventDefault();
      // Replace, not push: switching tabs is the same page, so Back leaves the PLP
      // (e.g. to the homepage) instead of stepping through every tab tried.
      window.location.replace(this.urlFor(option));
    });

    this.filterUpdateHandler = () => this.syncActive();
    document.addEventListener(FILTER_UPDATE_EVENT, this.filterUpdateHandler);
  }

  disconnectedCallback() {
    document.removeEventListener(FILTER_UPDATE_EVENT, this.filterUpdateHandler);
  }

  urlFor(option) {
    const params = new URLSearchParams(window.location.search);
    const wasActive = option.classList.contains('is-active');
    params.delete(this.param);
    // Categories differ by gender, so one picked on this side may not exist on the other.
    params.delete('filter.p.product_type');
    params.delete('page');
    if (!wasActive) {
      option.dataset.genderValues.split('|').forEach((value) => params.append(this.param, value));
    }
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  // A side is open when its own value is selected and the other side's isn't.
  syncActive() {
    const selected = new URLSearchParams(window.location.search).getAll(this.param);
    const ownValues = this.options.map((option) => option.dataset.genderOwn);
    this.options.forEach((option) => {
      const own = option.dataset.genderOwn;
      const isActive = selected.includes(own) && ownValues.every((value) => value === own || !selected.includes(value));
      option.classList.toggle('is-active', isActive);
      if (isActive) option.setAttribute('aria-current', 'true');
      else option.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('plp-gender-switch')) {
  customElements.define('plp-gender-switch', PlpGenderSwitch);
}
